<template>
    <div class="asset-detail" ref="container">
        <!-- 重置缩放按钮 -->
        <button v-if="scale !== 1" class="zoom-reset-btn" @click="resetZoom">
            重置缩放 ({{ Math.round(scale * 100) }}%)
        </button>

        <!-- 顶部横幅 -->
        <section class="bg-gradient-to-r from-[#0D47A1] to-[#1976D2] py-12 md:py-20 text-white">
            <div class="container mx-auto px-4 flex items-center justify-between">
                <div class="max-w-2xl">
                    <h2 class="text-2xl md:text-4xl font-bold mb-4 md:mb-6">数据资产详情</h2>
                    <p class="text-sm md:text-xl text-blue-100 leading-relaxed opacity-90">
                        平台面向企业用户，对合法取得的数据资源或数据产品提供登记、变更、转移、审核、颁发证书等功能。
                    </p>
                </div>
                <div class="hidden lg:block">
                    <span class="iconify text-[120px] md:text-[160px] text-white/20"
                        data-icon="ri:database-2-line"></span>
                </div>
            </div>
        </section>

        <!-- 面包屑导航 -->
        <div class="breadcrumb-container">
            <div class="container mx-auto px-4">
                <el-breadcrumb separator=">">
                    <el-breadcrumb-item :to="{ path: '/registration' }">数据登记</el-breadcrumb-item>
                    <el-breadcrumb-item>{{ assetInfo.assetName }}</el-breadcrumb-item>
                </el-breadcrumb>
            </div>
        </div>

        <!-- 主要内容区 -->
        <main class="main-content">
            <div class="container mx-auto px-4">
                <!-- 资产名称 -->
                <section class="section asset-name-section">
                    <h1 class="asset-name">
                        {{ assetInfo.assetName }}
                        <span v-if="isExpired(assetInfo.effectiveDate)"
                            class="expired-badge">已过期</span>
                        <span v-else-if="assetInfo.publicityEndTime == null"
                            class="expired-badge publicity-badge">公示中</span>
                    </h1>
                </section>

                <!-- 证书信息 -->
                <section class="section company-info">
                    <el-card class="detail-card">
                        <div slot="header" class="clearfix">
                            <span>证书信息</span>
                        </div>
                        <el-descriptions :column="isMobile ? 1 : 2" border>
                            <el-descriptions-item label="申请主体">{{ assetInfo.companyName }}</el-descriptions-item>
                            <el-descriptions-item label="统一社会信用代码">{{ assetInfo.socialCreditCode
                                }}</el-descriptions-item>
                            <el-descriptions-item label="公示开始时间">{{ assetInfo.publicityStartTime
                                }}</el-descriptions-item>
                            <el-descriptions-item label="公示截止时间">{{ assetInfo.publicityEndTime }}</el-descriptions-item>
                            <el-descriptions-item label="登记编号">{{ assetInfo.registrationId }}</el-descriptions-item>
                            <el-descriptions-item label="登记证书">
                                <el-link type="primary" @click="viewCertificate">
                                    {{ assetInfo.state === '已颁发证书' ? '查看证书' : '--' }}
                                </el-link>
                            </el-descriptions-item>
                            <el-descriptions-item label="颁发证书时间">{{ assetInfo.issueDate }}</el-descriptions-item>
                            <el-descriptions-item label="有效期至">
                                {{ assetInfo.effectiveDate }}
                                <span v-if="isExpired(assetInfo.effectiveDate)"
                                    style="display:inline-block;margin-left:8px;padding:1px 8px;background:#ff4d4f;color:#fff;border-radius:4px;font-size:12px;">已过期</span>
                                <span v-else-if="assetInfo.publicityEndTime == null"
                                    style="display:inline-block;margin-left:8px;padding:1px 8px;background:#faad14;color:#fff;border-radius:4px;font-size:12px;">公示中</span>
                            </el-descriptions-item>
                        </el-descriptions>
                    </el-card>
                </section>

                <!-- 基本信息 -->
                <section class="section asset-details">
                    <el-card class="detail-card">
                        <div slot="header" class="clearfix">
                            <span>基本信息</span>
                        </div>
                        <el-descriptions :column="1" border>
                            <el-descriptions-item label="名称">{{ assetInfo.assetName }}</el-descriptions-item>
                            <el-descriptions-item label="数据来源">{{ assetInfo.dataSource }}</el-descriptions-item>
                            <el-descriptions-item label="所属行业">{{ assetInfo.industry }}</el-descriptions-item>
                            <el-descriptions-item label="数据类型">{{ assetInfo.dataType }}</el-descriptions-item>
                            <el-descriptions-item label="数据覆盖地域">{{ assetInfo.region }}</el-descriptions-item>
                            <el-descriptions-item label="应用场景描述">{{ assetInfo.scenario }}</el-descriptions-item>
                            <el-descriptions-item label="简介">{{ assetInfo.description }}</el-descriptions-item>
                        </el-descriptions>
                    </el-card>

                    <!-- 数据属性 -->
                    <el-card class="detail-card">
                        <div slot="header" class="clearfix">
                            <span>数据属性</span>
                        </div>
                        <el-descriptions :column="isMobile ? 1 : 2" border>
                            <el-descriptions-item label="数据结构">{{ assetInfo.dataScaleType }}</el-descriptions-item>
                            <el-descriptions-item
                                v-if="assetInfo.dataScaleType === '复合型数据' || assetInfo.dataScaleType === '结构化数据'"
                                label="数据规模">{{ assetInfo.contactName }}</el-descriptions-item>
                            <el-descriptions-item
                                v-if="assetInfo.dataScaleType === '复合型数据' || assetInfo.dataScaleType === '结构化数据'"
                                label="存储空间">{{ assetInfo.storageUnit }}</el-descriptions-item>
                            <el-descriptions-item
                                v-if="assetInfo.dataScaleType === '复合型数据' || assetInfo.dataScaleType === '非结构化数据'"
                                label="文件数量">{{ assetInfo.fileCount }}</el-descriptions-item>
                            <el-descriptions-item label="数据期间">{{ assetInfo.dataPeriod }}</el-descriptions-item>
                            <el-descriptions-item label="更新周期">{{ assetInfo.updateCycle }}</el-descriptions-item>
                        </el-descriptions>
                    </el-card>

                    <!-- 产权信息 -->
                    <el-card class="detail-card">
                        <div slot="header" class="clearfix">
                            <span>产权信息</span>
                        </div>
                        <el-descriptions :column="1" border>
                            <el-descriptions-item style="color: rgb(21, 101, 192);" label="权利类型">
                <span class="rights-type-text" :class="{ 'mobile-rights-type': isMobile }">
                    {{ spacedRightsType }}
                </span>
                            </el-descriptions-item>
                            <el-descriptions-item label="产权期限">{{ assetInfo.propertyPeriod }}</el-descriptions-item>
                            <el-descriptions-item label="使用限制">{{ assetInfo.usageLimit }}</el-descriptions-item>
                        </el-descriptions>
                    </el-card>

                    <!-- 证书展示区域 -->
                    <el-card class="detail-card" v-if="assetInfo.state === '已颁发证书'">
                        <div slot="header" class="clearfix">
                            <span>证书详情</span>
                            <el-button type="text" @click="showFirstCertificate">(点击查看大图)</el-button>
                        </div>
                        <div class="certificate-container" ref="certificateContainer">
                            <div class="certificate-thumbnail" v-for="(cert, index) in certificates" :key="index"
                                @click="showCertificate(index)">
                                <img :src="cert.thumbnail" :alt="'证书' + (index + 1)">
                            </div>
                        </div>
                    </el-card>

                    <!-- 提出异议按钮 -->
                    <el-card class="detail-card objection-section">
                        <div slot="header" class="clearfix">
                            <span>提出异议</span>
                        </div>
                        <div class="objection-content">
                            <p class="objection-tip">如您对该数据资产有异议，请在此提交异议申请。</p>
                            <div class="objection-actions">
                                <el-button 
                                    type="primary" 
                                    @click="fileAppeal"
                                    :disabled="!isLoggedIn"
                                    icon="el-icon-warning-outline"
                                >
                                    提出异议申诉
                                </el-button>
                                <p v-if="!isLoggedIn" class="login-prompt-text">
                                    您需要登录后才能提出异议，<el-link type="primary" @click="goToLogin">立即登录</el-link>
                                </p>
                            </div>
                        </div>
                    </el-card>
                </section>
            </div>
        </main>

        <!-- 证书弹窗 -->
        <el-dialog :visible.sync="certificateDialogVisible" :fullscreen="isMobile">
            <img width="100%" :src="currentCertificate.fullImage" :alt="'证书详情'">
        </el-dialog>

        <!-- 异议申诉对话框 -->
        <div v-if="showAppealDialog" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
                <!-- 对话框头部 -->
                <div class="p-6 border-b border-gray-100">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center">
                            <div class="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center mr-4">
                                <span class="iconify text-red-500 text-xl" data-icon="ri:alert-line"></span>
                            </div>
                            <div>
                                <h3 class="text-xl font-bold text-gray-800">异议申诉</h3>
                                <p class="text-sm text-gray-500 mt-1">对数据资产 "<span class="font-medium text-[#1565C0]">{{ assetInfo.assetName }}</span>" 提出异议</p>
                            </div>
                        </div>
                        <button @click="closeAppealDialog"
                            class="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors">
                            <span class="iconify text-gray-400 text-xl hover:text-gray-600"
                                data-icon="ri:close-line"></span>
                        </button>
                    </div>
                </div>

                <!-- 对话框内容 -->
                <div class="p-6 max-h-[70vh] overflow-y-auto">
                    <form @submit.prevent="submitAppeal">
                        <!-- 基本信息展示 -->
                        <div class="mb-6 bg-blue-50/50 rounded-lg p-4 border border-blue-100">
                            <div class="grid grid-cols-2 gap-4">
                                <div>
                                    <p class="text-xs text-gray-500 mb-1">登记主体</p>
                                    <p class="font-medium text-gray-800">{{ assetInfo.companyName }}</p>
                                </div>
                                <div>
                                    <p class="text-xs text-gray-500 mb-1">统一社会信用代码</p>
                                    <p class="font-medium text-gray-800">{{ assetInfo.socialCreditCode }}</p>
                                </div>
                                <div>
                                    <p class="text-xs text-gray-500 mb-1">登记编号</p>
                                    <p class="font-medium text-gray-800">{{ assetInfo.registrationId }}</p>
                                </div>
                                <div>
                                    <p class="text-xs text-gray-500 mb-1">登记时间</p>
                                    <p class="font-medium text-gray-800">{{ formatDate(assetInfo.createTime) }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- 申诉类型选择 -->
                        <div class="mb-6">
                            <label class="block text-sm font-medium text-gray-700 mb-3">
                                <span class="text-red-500 mr-1">*</span>
                                申诉类型
                            </label>
                            <div class="grid grid-cols-4 gap-3">
                                <label v-for="type in appealTypes" :key="type.value"
                                    class="flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200"
                                    :class="appealForm.type === type.value
                                        ? 'border-[#1565C0] bg-blue-50 shadow-sm'
                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'">
                                    <input type="radio" v-model="appealForm.type" :value="type.value" class="hidden">
                                    <div class="w-10 h-10 rounded-full mb-3 flex items-center justify-center"
                                        :class="appealForm.type === type.value ? 'bg-[#1565C0]' : 'bg-gray-100'">
                                        <span class="iconify text-lg"
                                            :class="appealForm.type === type.value ? 'text-white' : 'text-gray-500'"
                                            :data-icon="type.icon"></span>
                                    </div>
                                    <span class="font-medium text-gray-800 text-sm mb-1">{{ type.label }}</span>
                                    <span class="text-xs text-gray-500 text-center">{{ type.description }}</span>
                                </label>
                            </div>
                        </div>

                        <!-- 申诉问题输入 -->
                        <div class="mb-6">
                            <div class="flex items-center justify-between mb-3">
                                <label class="block text-sm font-medium text-gray-700">
                                    <span class="text-red-500 mr-1">*</span>
                                    申诉问题描述
                                </label>
                                <span class="text-xs"
                                    :class="appealForm.content.length > 500 ? 'text-red-500' : 'text-gray-500'">
                                    {{ appealForm.content.length }}/500
                                </span>
                            </div>
                            <textarea v-model="appealForm.content" placeholder="请详细描述您的异议问题，包括具体事实、理由和相关依据..."
                                class="w-full h-40 px-4 py-3 border border-gray-300 rounded-lg text-sm resize-none outline-none focus:border-[#1565C0] focus:ring-2 focus:ring-blue-100 transition-all"
                                :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-100': appealError.content }"
                                @input="appealError.content = ''"></textarea>
                            <div v-if="appealError.content" class="text-red-500 text-xs mt-2 flex items-center">
                                <span class="iconify mr-1" data-icon="ri:error-warning-line"></span>
                                {{ appealError.content }}
                            </div>
                            <p class="text-xs text-gray-500 mt-2">建议详细描述，不少于20字</p>
                        </div>

                        <div class="grid grid-cols-2 gap-6">
                            <!-- 联系方式 -->
                            <div class="mb-6">
                                <label class="block text-sm font-medium text-gray-700 mb-3">联系方式</label>
                                <div class="relative">
                                    <span
                                        class="iconify absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                        data-icon="ri:contacts-line"></span>
                                    <input v-model="appealForm.contact" placeholder="请输入您的联系电话或邮箱"
                                        class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#1565C0] focus:ring-2 focus:ring-blue-100 transition-all">
                                </div>
                                <p class="text-xs text-gray-500 mt-2">填写后便于我们及时与您沟通处理进展</p>
                            </div>

                            <!-- 附件上传 -->
                            <div class="mb-6">
                                <label class="block text-sm font-medium text-gray-700 mb-3">相关证明材料</label>

                                <!-- 上传区域 -->
                                <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#1565C0] transition-colors cursor-pointer"
                                    @click="triggerFileUpload">
                                    <div
                                        class="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-2">
                                        <span class="iconify text-[#1565C0] text-xl"
                                            data-icon="ri:upload-cloud-2-line"></span>
                                    </div>
                                    <p class="text-sm text-gray-600 mb-1">点击上传证明材料</p>
                                    <p class="text-xs text-gray-400">支持图片、PDF、Word等格式，单个文件不超过10MB</p>
                                </div>

                                <input type="file" ref="fileInput" @change="handleFileUpload" class="hidden" multiple>
                            </div>
                        </div>

                        <!-- 已上传文件列表 -->
                        <div v-if="appealForm.files.length > 0" class="mb-6">
                            <label class="block text-sm font-medium text-gray-700 mb-3">已上传文件</label>
                            <div v-for="(file, index) in appealForm.files" :key="index"
                                class="border border-gray-200 rounded-lg p-3 bg-white hover:bg-gray-50 transition-colors">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center flex-1 min-w-0">
                                        <!-- 文件图标 -->
                                        <div
                                            class="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                                            <span class="iconify text-[#1565C0]" :data-icon="getFileIcon(file)"></span>
                                        </div>

                                        <!-- 文件信息 -->
                                        <div class="flex-1 min-w-0">
                                            <div class="flex items-center justify-between mb-1">
                                                <p class="text-sm font-medium text-gray-800 truncate">{{ file.name }}</p>
                                                <span class="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">{{ formatFileSize(file.size) }}</span>
                                            </div>

                                            <!-- 上传状态 -->
                                            <div class="flex items-center justify-between">
                                                <div class="flex items-center">
                                                    <span v-if="file.uploadStatus === 'success'"
                                                        class="text-xs text-green-600 flex items-center">
                                                        上传成功
                                                    </span>
                                                    <span v-else-if="file.uploadStatus === 'uploading'"
                                                        class="text-xs text-blue-600 flex items-center">
                                                        上传中...
                                                    </span>
                                                    <span v-else-if="file.uploadStatus === 'error'"
                                                        class="text-xs text-red-600 flex items-center">
                                                        上传失败
                                                    </span>
                                                    <span v-else class="text-xs text-gray-500">待上传</span>
                                                </div>

                                                <!-- 文件类型标签 -->
                                                <span class="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">{{ getFileType(file) }}</span>
                                            </div>

                                            <!-- 上传进度条 -->
                                            <div v-if="file.uploadStatus === 'uploading'" class="mt-2">
                                                <div class="w-full bg-gray-200 rounded-full h-1.5">
                                                    <div class="bg-[#1565C0] h-1.5 rounded-full transition-all duration-300"
                                                        :style="{ width: file.progress + '%' }"></div>
                                                </div>
                                                <p class="text-xs text-gray-500 text-right mt-1">{{ file.progress }}%</p>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- 操作按钮 -->
                                    <div class="ml-3 flex items-center space-x-2">
                                        <!-- 重新上传按钮（仅在上传失败时显示） -->
                                        <button v-if="file.uploadStatus === 'error'" @click.stop="retryUpload(index)"
                                            class="w-8 h-8 rounded-full hover:bg-blue-50 flex items-center justify-center transition-colors"
                                            title="重新上传">
                                            <span class="iconify text-blue-500" data-icon="ri:refresh-line"></span>
                                        </button>

                                        <!-- 删除按钮 -->
                                        <button @click.stop="removeFile(index)"
                                            class="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center transition-colors"
                                            title="删除文件">
                                            <span class="iconify text-red-500" data-icon="ri:close-line"></span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 注意事项 -->
                        <div class="mb-6 bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                            <div class="flex items-start">
                                <span class="iconify text-yellow-500 mr-3 mt-0.5"
                                    data-icon="ri:information-line"></span>
                                <div>
                                    <p class="text-sm font-medium text-yellow-800 mb-1">重要提示</p>
                                    <ul class="text-xs text-yellow-700 space-y-1">
                                        <li>• 请确保申诉内容真实、准确，不得捏造事实</li>
                                        <li>• 平台将在3个工作日内处理您的申诉</li>
                                        <li>• 处理结果将通过系统消息通知</li>
                                        <li>• 恶意申诉将承担相应法律责任</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                <!-- 对话框底部 -->
                <div class="p-6 border-t border-gray-100 bg-gray-50 rounded-b-xl">
                    <div class="flex justify-between items-center">
                        <div class="text-xs text-gray-500">
                            <span class="iconify inline-block mr-1" data-icon="ri:shield-check-line"></span>
                            您的信息将严格保密
                        </div>
                        <div class="flex space-x-3">
                            <button @click="closeAppealDialog"
                                class="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center">
                                <span class="iconify mr-2" data-icon="ri:close-circle-line"></span>
                                取消
                            </button>
                            <button @click="submitAppeal" :disabled="submittingAppeal"
                                class="px-6 py-2.5 bg-gradient-to-r from-[#1565C0] to-[#0D47A1] text-white rounded-lg font-medium hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
                                <span v-if="submittingAppeal" class="iconify animate-spin mr-2"
                                    data-icon="ri:loader-4-line"></span>
                                <span v-else class="iconify mr-2" data-icon="ri:send-plane-line"></span>
                                {{ submittingAppeal ? '提交中...' : '提交申诉' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 页脚 -->
        <footer class="footer">
            <div class="container mx-auto px-4">
                <div class="footer-content">
                    <div class="footer-section">
                        <p class="footer-title">数据资产全过程AI服务平台</p>
                        <p>邮箱: yuanshuqiang@xinxiusz.com</p>
                        <p>联系电话: 15831289727</p>
                        <p>地址: 保定市竞秀区时代路数字大厦一楼</p>
                    </div>
                    <div class="footer-section">
                        <div class="footer-links">
                            <router-link to="/" class="footer-link">官网首页</router-link>
                            <router-link to="/infoPublic" class="footer-link">信息公示</router-link>
                            <router-link to="/about" class="footer-link">关于平台</router-link>
                        </div>
                    </div>
                </div>
                <div class="footer-bottom">
                    <p>© 2025 数据资产全过程AI服务平台 版权所有 隐私政策 冀ICP备2025125156号</p>
                </div>
            </div>
        </footer>
    </div>
</template>

<script>
export default {
    name: 'AssetDetail',
    data() {
        return {
            scale: 1,
            minScale: 0.5,
            maxScale: 3,
            initialDistance: 0,
            isScrolling: false,
            touchStartX: 0,
            touchStartY: 0,
            touchStartTime: 0,

            assetInfo: {
                assetName: '',
                dataSource: '',
                industry: '',
                dataType: '',
                region: '',
                scenario: '',
                description: '',
                companyName: '',
                publicityStartTime: '',
                registrationId: '',
                socialCreditCode: '',
                publicityEndTime: '',
                issueDate: '',
                state: '',
                dataScaleType: '',
                fileCount: '',
                dataPeriod: '',
                updateCycle: '',
                propertyPeriod: '',
                usageLimit: '',
                qrCode: require('@/assets/img/qrcode.png')
            },

            certificates: [],
            certificateDialogVisible: false,
            currentCertificate: {},
            isMobile: window.innerWidth < 768,
            loginCheckInterval: null,

            // 异议申诉相关数据（参考WebsiteRegistration.vue）
            showAppealDialog: false,
            submittingAppeal: false,
            appealForm: {
                type: 'ownership', // 默认选择权属问题
                content: '',
                contact: '',
                files: []
            },
            appealError: {
                content: ''
            },
            appealTypes: [
                {
                    value: 'ownership',
                    label: '权属问题',
                    description: '对数据所有权、使用权有异议',
                    icon: 'ri:shield-keyhole-line'
                },
                {
                    value: 'authenticity',
                    label: '真实性',
                    description: '质疑数据来源或内容真实性',
                    icon: 'ri:file-search-line'
                },
                {
                    value: 'compliance',
                    label: '合规性',
                    description: '认为数据存在合规风险',
                    icon: 'ri:shield-check-line'
                },
                {
                    value: 'other',
                    label: '其他',
                    description: '其他类型的问题',
                    icon: 'ri:question-line'
                }
            ],
            showUploadSuccess: false,
            uploadSuccessTimer: null,

            // 登录状态
            isLoggedIn: false,
            avatarUrl: ''
        }
    },
    created() {
        this.checkLoginStatus();
        this.setupLoginListener();
        const pathSegments = this.$route.path.split('/');
        const registrationId = pathSegments[pathSegments.length - 1];
        this.fetchAssetInfo(registrationId);
        this.fetchCertificates(registrationId);

        // 监听窗口大小变化
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        if (this.loginCheckInterval) {
            clearInterval(this.loginCheckInterval);
        }
        window.removeEventListener('resize', this.handleResize);
    },
    computed: {
        spacedRightsType() {
            if (!this.assetInfo.rightsType) return '';
            // 按空格分割，然后用更多空格重新连接
            const rights = this.assetInfo.rightsType.split(' ').filter(item => item.trim());
            return rights.join('   '); // 使用三个空格分隔
        }
    },
    methods: {
        isExpired(effectiveDate) {
            if (!effectiveDate) return false
            const match = effectiveDate.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/)
            if (!match) return false
            const date = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]))
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            return date < today
        },

        handleResize() {
            this.isMobile = window.innerWidth < 768;
        },

        handleTouchStart(e) {
            if (e.touches.length === 1) {
                this.touchStartX = e.touches[0].clientX;
                this.touchStartY = e.touches[0].clientY;
                this.touchStartTime = Date.now();
                this.isScrolling = false;
            } else if (e.touches.length === 2) {
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                this.initialDistance = Math.sqrt(dx * dx + dy * dy);
            }
        },

        handleTouchMove(e) {
            if (e.touches.length === 2) {
                e.preventDefault();
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                const currentDistance = Math.sqrt(dx * dx + dy * dy);

                if (this.initialDistance > 0) {
                    const scaleFactor = currentDistance / this.initialDistance;
                    let newScale = this.scale * scaleFactor;
                    newScale = Math.max(this.minScale, Math.min(this.maxScale, newScale));

                    if (Math.abs(newScale - this.scale) > 0.01) {
                        this.scale = newScale;
                        this.$refs.container.style.transform = `scale(${this.scale})`;
                        this.$refs.container.style.transformOrigin = 'center top';
                    }
                }
                this.initialDistance = currentDistance;
            }
        },

        handleTouchEnd(e) {
            if (!this.isScrolling && e.changedTouches.length === 1) {
                const touchEndTime = Date.now();
                if (touchEndTime - this.touchStartTime < 300) {
                    // 点击处理
                }
            }
            if (e.touches.length < 2) {
                this.initialDistance = 0;
            }
        },

        resetZoom() {
            this.scale = 1;
            this.$refs.container.style.transform = 'scale(1)';
        },

        fetchAssetInfo(registrationId) {
            Promise.all([
                this.$axios.get('/web/page/dataAssets/query', { params: { registrationId } }),
                this.$axios.get('/web/page/certificate/query', { params: { registrationId } }),
                this.$axios.get('/web/page/companyInfo/query', { params: { registrationId } })
            ]).then(([assetsResponse, certificateResponse, companyResponse]) => {
                this.assetInfo = {
                    ...assetsResponse.data,
                    ...companyResponse.data,
                    ...certificateResponse.data,
                    dataPeriod: assetsResponse.data.creator,
                    propertyPeriod: assetsResponse.data.updater,
                    issueDate: certificateResponse.data.createTime,
                    state: assetsResponse.data.state,
                    qrCode: require('@/assets/img/qrcode.png')
                };
            }).catch(error => {
                console.error('获取信息失败:', error);
            });
        },

        checkLoginStatus() {
            const user = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || 'null');
            this.isLoggedIn = !!user;
            if (user) {
                this.avatarUrl = user.avatar;
            }
        },

        setupLoginListener() {
            window.addEventListener('storage', (event) => {
                if (event.key === 'user') {
                    this.checkLoginStatus();
                }
            });
            this.loginCheckInterval = setInterval(() => {
                this.checkLoginStatus();
            }, 1000);
        },

        viewCertificate() {
            this.scrollToCertificates();
        },

        scrollToCertificates() {
            this.$nextTick(() => {
                const container = this.$refs.certificateContainer;
                if (container) {
                    container.scrollIntoView({ behavior: 'smooth' });
                }
            });
        },

        showCertificate(index) {
            this.currentCertificate = this.certificates[index];
            this.certificateDialogVisible = true;
        },

        showFirstCertificate() {
            if (this.certificates.length > 0) {
                this.showCertificate(0);
            }
        },

        async fetchCertificates(registrationId) {
            try {
                const response = await this.$axios({
                    method: 'get',
                    url: '/web/page/certificate/view',
                    params: { registrationId },
                    responseType: 'blob'
                });
                const imageUrl = window.URL.createObjectURL(new Blob([response.data]));
                this.certificates = [{
                    thumbnail: imageUrl,
                    fullImage: imageUrl
                }];
                this.$once('hook:beforeDestroy', () => {
                    window.URL.revokeObjectURL(imageUrl);
                });
            } catch (error) {
                console.error('获取证书失败:', error);
            }
        },

        // 异议相关方法（参考WebsiteRegistration.vue）
        goToLogin() {
            this.$router.push('/login');
        },

        fileAppeal() {
            if (!this.isLoggedIn) {
                this.$message.warning('请先登录后再提出异议');
                this.goToLogin();
                return;
            }
            this.resetAppealForm();
            this.showAppealDialog = true;
        },

        closeAppealDialog() {
            this.showAppealDialog = false;
            this.resetAppealForm();
        },

        resetAppealForm() {
            this.appealForm = {
                type: 'ownership',
                content: '',
                contact: '',
                files: []
            };
            this.appealError = {
                content: ''
            };
            this.submittingAppeal = false;
        },

        validateAppealForm() {
            let isValid = true;

            // 验证问题描述
            if (!this.appealForm.content.trim()) {
                this.appealError.content = '请填写申诉问题描述';
                isValid = false;
            } else if (this.appealForm.content.trim().length < 20) {
                this.appealError.content = '问题描述至少需要20字';
                isValid = false;
            } else if (this.appealForm.content.length > 500) {
                this.appealError.content = '问题描述不能超过500字';
                isValid = false;
            }

            return isValid;
        },

        async submitAppeal() {
            if (!this.validateAppealForm()) {
                return;
            }

            try {
                this.submittingAppeal = true;

                // 构建提交数据
                const appealData = {
                    registrationId: this.assetInfo.registrationId,
                    assetName: this.assetInfo.assetName,
                    companyName: this.assetInfo.companyName,
                    appealType: this.appealForm.type,
                    appealContent: this.appealForm.content,
                    contactInfo: this.appealForm.contact,
                    files: this.appealForm.files
                };

                // 实际API调用
                // const response = await this.$axios.post('/web/appeal/submit', appealData);

                // if (response.data && response.data.code === 200) {
                if (true) {
                    this.$message.success('申诉提交成功！我们将在3个工作日内处理您的申诉');
                    this.closeAppealDialog();
                } else {
                    this.$message.error(response.data?.msg || '提交申诉失败');
                }

            } catch (error) {
                console.error('提交申诉失败:', error);
                this.$message.error('提交申诉失败，请稍后重试');
            } finally {
                this.submittingAppeal = false;
            }
        },

        triggerFileUpload() {
            this.$refs.fileInput.click();
        },

        async handleFileUpload(event) {
            const files = Array.from(event.target.files);

            // 验证文件大小和类型
            const validFiles = files.filter(file => {
                const maxSize = 10 * 1024 * 1024; // 10MB

                if (file.size > maxSize) {
                    this.$message.warning(`文件 "${file.name}" 超过10MB限制`);
                    return false;
                }

                // 检查文件类型
                const allowedExtensions = [
                    '.jpg', '.jpeg', '.png', '.gif', '.webp',
                    '.pdf',
                    '.doc', '.docx',
                    '.xls', '.xlsx'
                ];

                const fileName = file.name.toLowerCase();
                const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

                if (!isValidExtension) {
                    this.$message.warning(`文件 "${file.name}" 格式不支持，请上传图片、PDF、Word或Excel文件`);
                    return false;
                }

                return true;
            });

            if (validFiles.length === 0) {
                return;
            }

            // 为每个文件添加上传状态信息
            const filesWithStatus = validFiles.map(file => ({
                file: file, // 保留原始文件对象
                name: file.name,
                size: file.size,
                type: file.type,
                uploadStatus: 'pending', // pending, uploading, success, error
                progress: 0,
                id: Date.now() + Math.random().toString(36).substr(2, 9)
            }));

            // 添加到文件列表
            this.appealForm.files.push(...filesWithStatus);

            // 清空文件输入
            event.target.value = '';

            // 开始模拟上传
            this.simulateFileUpload(filesWithStatus);
        },

        simulateFileUpload(files) {
            files.forEach((file, index) => {
                const fileIndex = this.appealForm.files.findIndex(f => f.id === file.id);
                if (fileIndex === -1) return;

                // 更新状态为上传中
                                this.$set(this.appealForm.files[fileIndex], 'uploadStatus', 'uploading');

                // 模拟上传进度
                let progress = 0;
                const interval = setInterval(() => {
                    progress += Math.random() * 20 + 10; // 每次增加10-30%
                    if (progress >= 100) {
                        progress = 100;
                        clearInterval(interval);

                        // 模拟上传成功
                        setTimeout(() => {
                            this.$set(this.appealForm.files[fileIndex], 'uploadStatus', 'success');
                            this.$set(this.appealForm.files[fileIndex], 'progress', 100);

                            // 显示上传成功提示
                            this.showUploadSuccessMessage();
                        }, 300);
                    } else {
                        this.$set(this.appealForm.files[fileIndex], 'progress', Math.min(progress, 99));
                    }
                }, 200);
            });
        },

        showUploadSuccessMessage() {
            // 清除之前的定时器
            if (this.uploadSuccessTimer) {
                clearTimeout(this.uploadSuccessTimer);
            }

            this.showUploadSuccess = true;

            // 5秒后自动隐藏提示
            this.uploadSuccessTimer = setTimeout(() => {
                this.showUploadSuccess = false;
            }, 5000);
        },

        retryUpload(index) {
            const file = this.appealForm.files[index];
            this.$set(this.appealForm.files[index], 'uploadStatus', 'uploading');
            this.$set(this.appealForm.files[index], 'progress', 0);

            // 模拟重新上传
            let progress = 0;
            const interval = setInterval(() => {
                progress += Math.random() * 30 + 20; // 每次增加20-50%
                if (progress >= 100) {
                    progress = 100;
                    clearInterval(interval);

                    setTimeout(() => {
                        this.$set(this.appealForm.files[index], 'uploadStatus', 'success');
                        this.$set(this.appealForm.files[index], 'progress', 100);
                        this.showUploadSuccessMessage();
                    }, 300);
                } else {
                    this.$set(this.appealForm.files[index], 'progress', Math.min(progress, 99));
                }
            }, 200);
        },

        removeFile(index) {
            if (index >= 0 && index < this.appealForm.files.length) {
                this.appealForm.files.splice(index, 1);

                // 如果删除了所有文件，隐藏成功提示
                if (this.appealForm.files.length === 0) {
                    this.showUploadSuccess = false;
                    if (this.uploadSuccessTimer) {
                        clearTimeout(this.uploadSuccessTimer);
                    }
                }
            }
        },

        getFileIcon(fileObj) {
            // 处理文件对象
            const file = fileObj.file || fileObj;
            if (!file) return 'ri:file-line';

            const type = file.type || '';
            const name = file.name || '';

            if (type.includes('image') || /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(name)) {
                return 'ri:image-line';
            }
            if (type.includes('pdf') || name.toLowerCase().endsWith('.pdf')) {
                return 'ri:file-pdf-line';
            }
            if (type.includes('word') || type.includes('document') || /\.(doc|docx)$/i.test(name)) {
                return 'ri:file-word-line';
            }
            if (type.includes('excel') || type.includes('spreadsheet') || /\.(xls|xlsx)$/i.test(name)) {
                return 'ri:file-excel-line';
            }
            return 'ri:file-line';
        },

        getFileType(file) {
            // 修复文件类型判断逻辑
            if (!file) return '文件';

            // 优先使用文件对象的type属性
            if (file.type) {
                if (file.type.includes('image')) return '图片';
                if (file.type.includes('pdf')) return 'PDF';
                if (file.type.includes('word') || file.type.includes('document')) return 'Word文档';
                if (file.type.includes('excel') || file.type.includes('spreadsheet')) return 'Excel文档';
            }

            // 如果type不存在，尝试通过文件名判断
            if (file.name) {
                const extension = file.name.split('.').pop().toLowerCase();
                const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
                const pdfExtensions = ['pdf'];
                const wordExtensions = ['doc', 'docx'];
                const excelExtensions = ['xls', 'xlsx'];

                if (imageExtensions.includes(extension)) return '图片';
                if (pdfExtensions.includes(extension)) return 'PDF';
                if (wordExtensions.includes(extension)) return 'Word文档';
                if (excelExtensions.includes(extension)) return 'Excel文档';
            }

            return '文件';
        },

        formatFileSize(bytes) {
            // 修复文件大小格式化逻辑
            if (!bytes || isNaN(bytes) || bytes === 0) return '0 Bytes';

            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
        }
    }
}
</script>

<style scoped>
/* 基础样式 */
.asset-detail {
    font-family: 'Microsoft YaHei', sans-serif;
    color: #536a6b;
    background-color: #ffffff;
    line-height: 1.6;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    transition: transform 0.2s ease;
    transform-origin: center top;
    -webkit-text-size-adjust: 100%;
    user-select: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
}

.container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 15px;
}

/* 重置缩放按钮 */
.zoom-reset-btn {
    position: fixed;
    bottom: 20px;
    right: 20px;
    padding: 10px 20px;
    background-color: #1E88E5;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    z-index: 1000;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    font-size: 14px;
    transition: all 0.3s;
}

.zoom-reset-btn:hover {
    background-color: #1565C0;
    transform: scale(1.05);
}

/* 面包屑导航 */
.breadcrumb-container {
    background-color: #ffffff;
    padding: 15px 0;
    border-bottom: 1px solid #e0e0e0;
}

/* 主要内容区 */
.main-content {
    flex: 1;
    padding: 20px 0;
}

.section {
    margin-bottom: 30px;
}

.asset-name-section {
    text-align: center;
    margin-bottom: 30px;
}

.asset-name {
    font-size: 1.5rem;
    color: #050708;
    font-weight: 600;
    margin: 0;
}

.expired-badge {
    display: inline-block;
    margin-left: 10px;
    padding: 2px 10px;
    background-color: #ff4d4f;
    color: #fff;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    vertical-align: middle;
    line-height: 1.6;
}

.publicity-badge {
    background-color: #faad14;
    color: #fff;
}

/* 卡片样式 */
.detail-card {
    margin-bottom: 20px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.detail-card>>>.el-card__header {
    background-color: #e2e4e7;
    color: rgb(1, 1, 1);
    font-size: 16px;
    font-weight: 500;
    padding: 15px 20px;
}

.detail-card>>>.el-descriptions {
    width: 100%;
}

.detail-card>>>.el-descriptions-item__label {
    background-color: #fcfcfc;
    color: #000000;
    font-weight: 500;
}

.detail-card>>>.el-descriptions-item__content {
    color: #6b6b6b;
}

/* 证书展示 */
.certificate-container {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-top: 20px;
    justify-content: center;
}

/* 证书缩略图 */
.certificate-thumbnail {
    width: 100%;
    max-width: 300px;
    height: 400px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: #fff;
}

.certificate-thumbnail:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: #1E88E5;
}

.certificate-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: #f8f9fa;
}

/* 证书弹窗 */
.el-dialog {
    max-width: 90%;
}

.el-dialog__body {
    padding: 0;
    text-align: center;
    background-color: #f8f9fa;
}

.el-dialog__body img {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
}

/* 异议区域样式 */
.objection-section {
    margin-top: 30px;
}

.objection-content {
    padding: 20px;
}

.objection-tip {
    color: #666;
    font-size: 14px;
    margin-bottom: 20px;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 4px;
    border-left: 4px solid #1E88E5;
}

.objection-actions {
    text-align: center;
}

.login-prompt-text {
    margin-top: 15px;
    color: #666;
    font-size: 14px;
}

/* 异议申诉对话框样式 */
.fixed.inset-0 {
    animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.bg-white.rounded-xl {
    animation: slideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* 文件上传区域悬停效果 */
.border-2.border-dashed:hover {
    background-color: rgba(21, 101, 192, 0.05);
}

/* 响应式调整 */
@media (max-width: 768px) {
    .certificate-container {
        gap: 10px;
        margin-top: 15px;
    }

    .certificate-thumbnail {
        max-width: 100%;
        height: 300px;
        margin: 0 auto;
    }

    .el-dialog {
        width: 95% !important;
        margin: 10px !important;
    }

    .el-dialog__body img {
        max-height: 70vh;
    }

    .objection-content {
        padding: 15px;
    }

    /* 移动端对话框调整 */
    .fixed.inset-0 {
        padding: 1rem;
        align-items: flex-end;
    }

    .bg-white.rounded-xl {
        width: 100%;
        max-width: 100%;
        border-radius: 1rem 1rem 0 0;
        animation: slideUpMobile 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }

    @keyframes slideUpMobile {
        from {
            transform: translateY(100%);
        }
        to {
            transform: translateY(0);
        }
    }

    /* 申诉类型网格调整为2列 */
    .grid.grid-cols-4 {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 480px) {
    .certificate-container {
        gap: 8px;
    }

    .certificate-thumbnail {
        height: 250px;
        border-radius: 6px;
    }

    .el-dialog {
        width: 100% !important;
        margin: 0 !important;
        border-radius: 0;
    }

    .el-dialog__body {
        padding: 10px;
    }

    .objection-content {
        padding: 10px;
    }

    /* 申诉类型网格调整为1列 */
    .grid.grid-cols-4 {
        grid-template-columns: 1fr;
    }

    /* 联系方式网格调整为1列 */
    .grid.grid-cols-2 {
        grid-template-columns: 1fr;
    }
}

/* 平板设备 */
@media (min-width: 769px) and (max-width: 1024px) {
    .certificate-container {
        gap: 20px;
    }

    .certificate-thumbnail {
        max-width: 280px;
        height: 380px;
    }

    .fixed.inset-0 .bg-white.rounded-xl {
        max-width: 90%;
    }
}

/* 大屏幕设备 */
@media (min-width: 1200px) {
    .certificate-container {
        gap: 25px;
    }

    .certificate-thumbnail {
        max-width: 350px;
        height: 500px;
    }
}

/* 触摸设备优化 */
@media (hover: none) and (pointer: coarse) {
    .certificate-thumbnail:hover {
        transform: none;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .certificate-thumbnail:active {
        transform: scale(0.98);
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    }
}

/* 暗色模式支持 */
@media (prefers-color-scheme: dark) {
    .certificate-thumbnail {
        background-color: #2d3748;
        border-color: #4a5568;
    }

    .certificate-thumbnail img {
        background-color: #1a202c;
    }

    .el-dialog__body {
        background-color: #1a202c;
    }
}

/* 打印样式 */
@media print {
    .certificate-container {
        display: block;
    }

    .certificate-thumbnail {
        page-break-inside: avoid;
        break-inside: avoid;
        max-width: 100%;
        height: auto;
        margin-bottom: 20px;
        box-shadow: none;
        border: 1px solid #ccc;
    }

    .certificate-thumbnail img {
        object-fit: contain;
    }
}

/* 页脚样式 - 保持原始样式 */
.footer {
    background: #245a97;
    color: white;
    padding: 40px 0 20px;
    margin-top: 40px;
}

.footer-content {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 15px;
}

.footer-section {
    margin: 10px 0;
    flex-basis: 30%;
}

.footer-title {
    font-weight: bold;
    margin-bottom: 15px;
    font-size: 20px;
    color: #ffffff;
}

.footer-section p {
    margin: 8px 0;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
}

.footer-links {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.footer-link {
    color: white;
    text-decoration: none;
    font-size: 14px;
    display: block;
    margin-bottom: 8px;
}

.footer-link:hover {
    text-decoration: underline;
}

.footer-bottom {
    text-align: center;
    width: 100%;
    margin-top: 30px;
    font-size: 12px;
    padding-top: 15px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    color: #ffffff;
}

.footer-bottom p {
    margin: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .footer {
        padding: 30px 0 15px;
        margin-top: 30px;
    }

    .footer-content {
        flex-direction: column;
    }

    .footer-section {
        flex-basis: 100%;
        margin-bottom: 20px;
        text-align: center;
    }

    .footer-title {
        font-size: 18px;
    }

    .footer-section p {
        font-size: 13px;
    }

    .footer-links {
        flex-direction: row;
        justify-content: center;
        flex-wrap: wrap;
        gap: 15px;
    }

    .footer-link {
        margin: 0 10px 0 0;
    }

    .footer-bottom {
        margin-top: 20px;
        font-size: 11px;
        padding-top: 12px;
    }
}

@media (max-width: 480px) {
    .footer {
        padding: 25px 0 12px;
        margin-top: 25px;
    }

    .footer-title {
        font-size: 16px;
        margin-bottom: 12px;
    }

    .footer-section p {
        font-size: 12px;
        margin: 6px 0;
    }

    .footer-links {
        gap: 12px;
    }

    .footer-link {
        font-size: 13px;
    }

    .footer-bottom {
        margin-top: 15px;
        font-size: 10px;
        padding-top: 10px;
    }
}

/* 权利类型文本样式 */
.rights-type-text {
    color: rgb(21, 101, 192);
    font-weight: 500;
    font-size: 15px;
    white-space: pre;
}

/* 移动端权利类型样式 */
.mobile-rights-type {
    white-space: normal !important;
    word-break: break-word;
    display: block;
    line-height: 1.5;
}

/* 响应式调整 */
@media (max-width: 768px) {
    .rights-type-text {
        font-size: 14px;
        white-space: normal;
        word-break: break-word;
        display: block;
        line-height: 1.5;
    }
}
</style>